/**
 * one-thing-celebration.tsx — batch #4 (#8) ONE Thing "done" celebration.
 *
 * A short flash banner that fires the moment the client checks off today's
 * ONE Thing in the daily check-in. Mirrors the Daily WIN celebration's
 * brand-styled pop/flame animation, but is its OWN component with its own
 * copy so it never touches the WIN once-per-day guard.
 *
 * Self-dismisses after 4s and calls `onDismiss` so the parent can reset its
 * trigger (re-checking fires it again).
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  /** The declared ONE Thing — empty / whitespace renders nothing. */
  oneThingText: string | null | undefined;
  /** Called once the banner auto-dismisses so the parent can reset its trigger. */
  onDismiss: () => void;
}

const AUTO_DISMISS_MS = 4000;

export function OneThingCelebration({ oneThingText, onDismiss }: Props): JSX.Element | null {
  const { t } = useTranslation();
  const trimmed = (oneThingText ?? '').trim();

  useEffect(() => {
    const timer = window.setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [onDismiss]);

  if (trimmed.length === 0) return null;

  return (
    <div
      data-testid="one-thing-celebration"
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 top-s5 z-50 flex justify-center pointer-events-none"
    >
      <div
        className="
          relative max-w-md mx-s4
          rounded-2xl border border-flame/40 bg-bone shadow-xl
          px-s5 py-s4 pointer-events-auto
          animate-[oneThingPop_0.55s_ease-out]
        "
      >
        <span
          aria-hidden="true"
          className="
            absolute -top-s4 left-1/2 -translate-x-1/2
            text-[40px] leading-none select-none
            animate-[oneThingBurst_0.9s_ease-out]
          "
        >
          🎯
        </span>
        <div className="text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-flame-ember mb-s1">
            {t('checkIn.oneThing.celebration.eyebrow')}
          </p>
          <h3 className="font-display text-display-sm text-ink leading-tight">
            {t('checkIn.oneThing.celebration.headline')}
          </h3>
          <p className="mt-s2 font-display text-base text-ink italic line-clamp-3">
            &ldquo;{trimmed}&rdquo;
          </p>
          <p className="mt-s3 font-mono text-[10px] text-ink/50">
            {t('checkIn.oneThing.celebration.tagline')}
          </p>
        </div>
      </div>
      <style>{`
        @keyframes oneThingPop {
          0%   { transform: translateY(-12px) scale(0.92); opacity: 0; }
          60%  { transform: translateY(0) scale(1.04); opacity: 1; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes oneThingBurst {
          0%   { transform: translate(-50%, 8px) scale(0.4) rotate(-12deg); opacity: 0; }
          40%  { transform: translate(-50%, -2px) scale(1.2) rotate(6deg); opacity: 1; }
          100% { transform: translate(-50%, 0) scale(1) rotate(0deg); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
